import React, { useState, useEffect } from 'react';
import { 
  Tag, Settings, Plus, Edit2, Trash2, Save, X, Check,
  FolderTree, Image, Video, Music, FileText, Clock, Zap, Star,
  ChevronDown, ChevronRight, GripVertical
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const AdminP2PSettings = () => {
  const [activeTab, setActiveTab] = useState('tiers');
  const [tiers, setTiers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  const [newCategory, setNewCategory] = useState({ name: '', icon: 'Package', parent_id: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tiersRes, catsRes] = await Promise.all([
        api.get('/admin/p2p/tiers'),
        api.get('/admin/p2p/categories')
      ]);
      setTiers(tiersRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTier = async (tier) => {
    try {
      await api.put(`/admin/p2p/tier/${tier.id}`, {
        name: tier.name,
        price: tier.price,
        value_limit: tier.value_limit,
        max_images: tier.max_images,
        allow_audio: tier.allow_audio,
        allow_video: tier.allow_video,
        allow_pdf: tier.allow_pdf,
        allow_url: tier.allow_url,
        allow_timer: tier.allow_timer,
        allow_custom_buttons: tier.allow_custom_buttons,
        priority_listing: tier.priority_listing,
        is_active: tier.is_active
      });
      toast.success('Tier updated successfully');
      setEditingTier(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update tier');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/p2p/category', newCategory);
      toast.success('Category created');
      setShowAddCategory(false);
      setNewCategory({ name: '', icon: 'Package', parent_id: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create category');
    }
  };

  const handleUpdateCategory = async (category) => {
    try {
      await api.put(`/admin/p2p/category/${category.id}`, {
        name: category.name,
        icon: category.icon,
        is_active: category.is_active
      });
      toast.success('Category updated');
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm('Are you sure? This will deactivate the category.')) return;
    try {
      await api.delete(`/admin/p2p/category/${categoryId}`);
      toast.success('Category removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const toggleExpand = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const TIER_COLORS = {
    SILVER: 'from-gray-400 to-gray-600',
    GOLD: 'from-yellow-400 to-yellow-600',
    DIAMOND: 'from-cyan-400 to-blue-600',
    VIP: 'from-purple-400 to-purple-600',
    PREMIUM: 'from-pink-400 to-rose-600'
  };

  const ICON_OPTIONS = [
    'Package', 'Download', 'ShoppingCart', 'Car', 'Smartphone', 'Home', 
    'Gem', 'User', 'Star', 'RefreshCw', 'Settings', 'Heart', 'Gift', 'Zap'
  ];

  // Organize categories into parent/children
  const parentCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Settings className="w-8 h-8 text-primary" />
            P2P Marketplace Settings
          </h1>
          <p className="text-muted-foreground mt-1">Manage pass tiers and categories</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('tiers')}
            className={`px-6 py-3 rounded-xl font-medium flex items-center gap-2 ${
              activeTab === 'tiers' ? 'bg-primary text-primary-foreground' : 'bg-white/5 hover:bg-white/10'
            }`}
          >
            <Tag className="w-5 h-5" />
            Pass Tiers
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-6 py-3 rounded-xl font-medium flex items-center gap-2 ${
              activeTab === 'categories' ? 'bg-primary text-primary-foreground' : 'bg-white/5 hover:bg-white/10'
            }`}
          >
            <FolderTree className="w-5 h-5" />
            Categories
          </button>
        </div>

        {/* Pass Tiers Tab */}
        {activeTab === 'tiers' && (
          <div className="space-y-4">
            {tiers.map(tier => (
              <div key={tier.id} className="glass-card overflow-hidden">
                {/* Header */}
                <div className={`bg-gradient-to-r ${TIER_COLORS[tier.name]} p-4 flex items-center justify-between`}>
                  <div className="flex items-center gap-4">
                    <Tag className="w-8 h-8 text-white" />
                    <div>
                      <h3 className="text-xl font-bold text-white">{tier.name}</h3>
                      <p className="text-white/70 text-sm">Max Value: ₹{tier.value_limit?.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl font-bold text-white">₹{tier.price}</span>
                    <button
                      onClick={() => setEditingTier(editingTier?.id === tier.id ? null : { ...tier })}
                      className="p-2 bg-white/20 rounded-lg hover:bg-white/30"
                    >
                      <Edit2 className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>

                {/* Edit Form */}
                {editingTier?.id === tier.id && (
                  <div className="p-6 border-t border-white/10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium mb-2">Price (₹)</label>
                        <input
                          type="number"
                          value={editingTier.price}
                          onChange={(e) => setEditingTier({ ...editingTier, price: parseFloat(e.target.value) })}
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Value Limit (₹)</label>
                        <input
                          type="number"
                          value={editingTier.value_limit}
                          onChange={(e) => setEditingTier({ ...editingTier, value_limit: parseFloat(e.target.value) })}
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Max Images</label>
                        <input
                          type="number"
                          value={editingTier.max_images}
                          onChange={(e) => setEditingTier({ ...editingTier, max_images: parseInt(e.target.value) })}
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={editingTier.is_active}
                            onChange={(e) => setEditingTier({ ...editingTier, is_active: e.target.checked })}
                            className="w-5 h-5 rounded"
                          />
                          <span>Active</span>
                        </label>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-6">
                      {[
                        { key: 'allow_audio', label: 'Audio', icon: Music },
                        { key: 'allow_video', label: 'Video', icon: Video },
                        { key: 'allow_pdf', label: 'PDF', icon: FileText },
                        { key: 'allow_url', label: 'URL', icon: Star },
                        { key: 'allow_timer', label: 'Timer', icon: Clock },
                        { key: 'allow_custom_buttons', label: 'Custom Btns', icon: Zap },
                      ].map(({ key, label, icon: Icon }) => (
                        <label key={key} className="flex items-center gap-2 p-3 bg-white/5 rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editingTier[key]}
                            onChange={(e) => setEditingTier({ ...editingTier, [key]: e.target.checked })}
                            className="w-4 h-4 rounded"
                          />
                          <Icon className="w-4 h-4" />
                          <span className="text-sm">{label}</span>
                        </label>
                      ))}
                    </div>

                    <div className="flex items-center gap-2 mb-4">
                      <input
                        type="checkbox"
                        checked={editingTier.priority_listing}
                        onChange={(e) => setEditingTier({ ...editingTier, priority_listing: e.target.checked })}
                        className="w-5 h-5 rounded"
                      />
                      <span className="font-medium">Priority Listing (Featured at top)</span>
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => handleUpdateTier(editingTier)}
                        className="flex items-center gap-2 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                      <button
                        onClick={() => setEditingTier(null)}
                        className="px-6 py-2 bg-white/10 rounded-lg hover:bg-white/20"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Features Summary */}
                {editingTier?.id !== tier.id && (
                  <div className="p-4 flex flex-wrap gap-3">
                    <span className="px-3 py-1 bg-white/5 rounded-full text-sm flex items-center gap-1">
                      <Image className="w-4 h-4" /> {tier.max_images} Images
                    </span>
                    {tier.allow_audio && <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm flex items-center gap-1"><Music className="w-4 h-4" /> Audio</span>}
                    {tier.allow_video && <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm flex items-center gap-1"><Video className="w-4 h-4" /> Video</span>}
                    {tier.allow_pdf && <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm flex items-center gap-1"><FileText className="w-4 h-4" /> PDF</span>}
                    {tier.allow_timer && <span className="px-3 py-1 bg-blue-500/20 text-blue-500 rounded-full text-sm flex items-center gap-1"><Clock className="w-4 h-4" /> Timer</span>}
                    {tier.allow_custom_buttons && <span className="px-3 py-1 bg-purple-500/20 text-purple-500 rounded-full text-sm flex items-center gap-1"><Zap className="w-4 h-4" /> Custom Btns</span>}
                    {tier.priority_listing && <span className="px-3 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-sm flex items-center gap-1"><Star className="w-4 h-4" /> Priority</span>}
                    {!tier.is_active && <span className="px-3 py-1 bg-red-500/20 text-red-500 rounded-full text-sm">Inactive</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <p className="text-muted-foreground">{categories.length} categories</p>
              <button
                onClick={() => setShowAddCategory(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl"
              >
                <Plus className="w-4 h-4" />
                Add Category
              </button>
            </div>

            <div className="glass-card divide-y divide-white/5">
              {parentCategories.map(category => {
                const subcategories = getSubcategories(category.id);
                const isExpanded = expandedCategories[category.id];

                return (
                  <div key={category.id}>
                    <div className="p-4 flex items-center justify-between hover:bg-white/5">
                      <div className="flex items-center gap-3">
                        {subcategories.length > 0 && (
                          <button onClick={() => toggleExpand(category.id)} className="p-1">
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        )}
                        {subcategories.length === 0 && <div className="w-6" />}
                        
                        {editingCategory?.id === category.id ? (
                          <input
                            type="text"
                            value={editingCategory.name}
                            onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                            className="px-3 py-1 bg-white/10 border border-white/20 rounded-lg"
                          />
                        ) : (
                          <span className="font-medium">{category.name}</span>
                        )}
                        
                        {!category.is_active && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-500 rounded text-xs">Inactive</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {editingCategory?.id === category.id ? (
                          <>
                            <button
                              onClick={() => handleUpdateCategory(editingCategory)}
                              className="p-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingCategory(null)}
                              className="p-2 bg-white/10 rounded-lg hover:bg-white/20"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setNewCategory({ ...newCategory, parent_id: category.id });
                                setShowAddCategory(true);
                              }}
                              className="p-2 text-blue-500 hover:bg-blue-500/10 rounded-lg"
                              title="Add Subcategory"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setEditingCategory({ ...category })}
                              className="p-2 hover:bg-white/10 rounded-lg"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Subcategories */}
                    {isExpanded && subcategories.length > 0 && (
                      <div className="bg-white/5">
                        {subcategories.map(sub => (
                          <div key={sub.id} className="p-4 pl-12 flex items-center justify-between border-t border-white/5">
                            <div className="flex items-center gap-3">
                              <span className="text-muted-foreground">└</span>
                              {editingCategory?.id === sub.id ? (
                                <input
                                  type="text"
                                  value={editingCategory.name}
                                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                                  className="px-3 py-1 bg-white/10 border border-white/20 rounded-lg"
                                />
                              ) : (
                                <span>{sub.name}</span>
                              )}
                              {!sub.is_active && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-500 rounded text-xs">Inactive</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {editingCategory?.id === sub.id ? (
                                <>
                                  <button onClick={() => handleUpdateCategory(editingCategory)} className="p-2 bg-green-500/20 text-green-500 rounded-lg">
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => setEditingCategory(null)} className="p-2 bg-white/10 rounded-lg">
                                    <X className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => setEditingCategory({ ...sub })} className="p-2 hover:bg-white/10 rounded-lg">
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => handleDeleteCategory(sub.id)} className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {showAddCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">
                {newCategory.parent_id ? 'Add Subcategory' : 'Add Category'}
              </h3>
              <button onClick={() => { setShowAddCategory(false); setNewCategory({ name: '', icon: 'Package', parent_id: null }); }} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              {newCategory.parent_id && (
                <div className="p-3 bg-blue-500/10 rounded-lg text-sm">
                  Adding subcategory under: <strong>{categories.find(c => c.id === newCategory.parent_id)?.name}</strong>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-2">Category Name</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="e.g., Smartphones"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Icon</label>
                <select
                  value={newCategory.icon}
                  onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                >
                  {ICON_OPTIONS.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-semibold"
              >
                Create Category
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminP2PSettings;
